import { useCallback, useEffect, useRef } from 'react';

/**
 * Erkennt eine Long-Press-Geste (Pointer-Events) auf einem Ziel-Element.
 *
 * `handlers` wird auf das Ziel-Element gespreadet. Löst `onLongPress()` aus, wenn der
 * Pointer `delayMs` ruhig liegt; eine Bewegung über `moveTolerancePx` (Scrollen) oder ein
 * Loslassen vor Ablauf bricht ab. Nach einem ausgelösten Long-Press unterdrückt der Hook
 * den unmittelbar folgenden Click (Browser feuert nach `pointerup` sonst zusätzlich
 * `click`) — dafür `onClickCapture` mit auf das Element spreaden.
 */
export function useLongPress(onLongPress, { delayMs = 450, moveTolerancePx = 10 } = {}) {
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const suppressClickRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Unmount während eines laufenden Drucks (Karte gefiltert/entfernt, Navigation) darf den
  // Timer nicht überleben lassen — sonst feuert onLongPress() gegen eine stale Closure.
  useEffect(() => () => clearTimer(), [clearTimer]);

  const onPointerDown = useCallback((event) => {
    startRef.current = { x: event.clientX, y: event.clientY };
    // Folgte dem letzten Long-Press kein Click (Finger weggezogen), darf die Sperre nicht den
    // Click dieses neuen Drucks schlucken.
    suppressClickRef.current = false;
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      suppressClickRef.current = true;
      onLongPress();
    }, delayMs);
  }, [clearTimer, delayMs, onLongPress]);

  const onPointerMove = useCallback((event) => {
    if (!startRef.current) return;
    const dx = event.clientX - startRef.current.x;
    const dy = event.clientY - startRef.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > moveTolerancePx) {
      clearTimer();
    }
  }, [clearTimer, moveTolerancePx]);

  const onPointerUp = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onPointerCancel = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onContextMenu = useCallback((event) => {
    event.preventDefault();
  }, []);

  const onClickCapture = useCallback((event) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onContextMenu,
    onClickCapture,
  };
}
